import { isObjectRecord } from "../typeGuards/isObjectRecord";
import type { ArsListingColumn, ArsTextAlignment } from "./interface";

/**
 * Validates if a value is a valid ArsTextAlignment.
 *
 * @param value - Unknown value to validate
 * @returns True when value is "left", "center", or "right"
 */
function isArsTextAlignment(value: unknown): value is ArsTextAlignment {
  return value === "left" || value === "center" || value === "right";
}

/**
 * Validates if a value is an array of variable references.
 *
 * @param value - Unknown value to validate
 * @returns True when value is an array of objects with dataset and variable strings
 */
function isVariableArray(value: unknown): value is { dataset: string; variable: string }[] {
  if (!Array.isArray(value)) return false;

  /* istanbul ignore next Check for valid variable array structure */
  for (const item of value) {
    if (!isObjectRecord(item)) return false;
    if (typeof item.dataset !== "string") return false;
    if (typeof item.variable !== "string") return false;
  }

  return true;
}

/**
 * Type guard for ArsListing column definitions.
 *
 * @param obj - Unknown value to validate
 * @param isT - Optional type guard for the generic type T used in displayPattern and headerText
 * @returns True when value can be treated as ArsListingColumn
 */
export const isArsListingColumn = <T = string>(
  obj: unknown,
  isT: (value: unknown) => value is T = (value: unknown): value is T => typeof value === "string",
): obj is ArsListingColumn => {
  // Check object
  if (!isObjectRecord(obj)) return false;

  // Check required properties
  if (typeof obj.id !== "string") return false;
  if (typeof obj.columnWidth !== "number") return false;
  if (obj.displayPattern === undefined || obj.displayPattern === null) return false;
  if (Array.isArray(obj.displayPattern) && obj.displayPattern.some((item) => !isT(item)))
    return false;
  if (!Array.isArray(obj.displayPattern) && !isT(obj.displayPattern)) return false;

  if (obj.headerText === undefined || obj.headerText === null) return false;
  if (!isT(obj.headerText)) return false;

  if (typeof obj.resultPattern !== "string") return false;
  if (!isVariableArray(obj.variables)) return false;

  // Check optional properties if present
  if (obj.bodyTextAlignment !== undefined && !isArsTextAlignment(obj.bodyTextAlignment)) {
    return false;
  }
  if (obj.headerHeight !== undefined && typeof obj.headerHeight !== "number") {
    return false;
  }
  if (obj.headerTextAlignment !== undefined && !isArsTextAlignment(obj.headerTextAlignment)) {
    return false;
  }

  // Everything is good
  return true;
};
